import { memo, useCallback, useContext, useMemo, useState } from 'react';
import { createId } from '@paralleldrive/cuid2';
import type { AccountProfileMessage as AccountProfileData } from '@trinity/chat';
import { ChatHintType } from '@trinity/chat';
import { LearnMore } from '@trinity/chat-components/components/Chat/LearnMore';
import { getCitationsDisplayCount } from '@trinity/chat-components/components/Chat.Message.Citations/utils';
import { MessageQueryHint } from '@trinity/chat-components/components/presentation/Message.QueryHint';
import { useGroupFeatures } from '@trinity/chat-components/containers/Chat';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient';
import { useCopyToClipboard } from '@trinity/chat-components/hooks/useCopyToClipboard';
import { ActiveChatSessionContext, CitationsContext, MessageCitationsToggleContext } from '../context';
import { useSelectedQueryParentIdentifier } from '../hooks/useSelectedQueryParentIdentifier';
import { useSetChatInputText } from '../hooks/useSetChatInputText';
import { useSubmitQueryMutation } from '../hooks/useSubmitQueryMutation';
import { ProfileAccordionContainer } from '../KolSearch.Profile.Accordion';
import { AITooltip } from '../KolSearch.Profile.AITooltip';
import { CopyButton } from '../Message.CopyToClipboard';
import { ReadAloudButton } from '../Message.ReadAloud';
import { BasicSystemUserMessage } from '../Message.System.Basic';
import { buildQuickActions } from './quick-actions';
import {
  Copy,
  Footer,
  Guidelines,
  Label,
  MessageBody,
  MessageContainer,
  ProfileItem,
  QuickActionsList,
  SectionContent,
  SmallHint,
  StyledCitationsPane,
  StyledMessageCitations,
  StyledRelatedQuestions,
} from './shared-styles';
import { renderProfileWidget } from './widgets';

type Props = {
  followupQuestions: string[];
  profile: AccountProfileData;
  queryId: number;
  queryIdentifier: string;
};

function buildSectionLearnMoreQuestion(profileName: string, sectionName: string): string {
  return `I would like to learn more about ${profileName} related to ${sectionName}.`;
}

export const AccountProfileMessage = memo(
  ({ followupQuestions, profile, queryId, queryIdentifier }: Props) => {
    const [hoveringSection, setHoveringSection] = useState<string | null>(null);
    const [citationsOpen, toggleCitationsOpen] = useContext(MessageCitationsToggleContext);
    const citations = useContext(CitationsContext);
    const chat = useContext(ActiveChatSessionContext);
    const apiClient = useChatApiClient();

    const { copyChatMessages } = useGroupFeatures();
    const copyToClipboard = useCopyToClipboard();
    const setChatInputText = useSetChatInputText();
    const { mutate: submitQuery } = useSubmitQueryMutation();
    const getSelectedQueryParentIdentifier = useSelectedQueryParentIdentifier();

    const quickActions = useMemo(() => buildQuickActions(profile.quickActions ?? []), [profile.quickActions]);
    const showDirectSubmitLearnMore = profile.messageActions.learnMoreDirectSubmit;

    const handleCopy = useCallback(() => {
      return apiClient.queries
        .getQueryResponseText({
          chatInstanceId: chat.id,
          queryId,
          tts: false,
        })
        .then(copyToClipboard);
    }, [apiClient.queries, chat.id, copyToClipboard, queryId]);

    const onDirectSubmitLearnMore = useCallback(
      (question: string) => {
        const trimmedQuestion = question.trim();
        if (!trimmedQuestion) {
          return;
        }

        const parentQueryIdentifier = getSelectedQueryParentIdentifier();

        submitQuery({
          identifier: createId(),
          parentQueryIdentifier: parentQueryIdentifier ?? null,
          query: trimmedQuestion,
          action: null,
        });
      },
      [getSelectedQueryParentIdentifier, submitQuery]
    );

    return (
      <MessageContainer>
        <BasicSystemUserMessage>
          <MessageBody>
            <Copy>Sure, here is the account profile for "{profile.name}"</Copy>

            <ProfileItem>
              <Label>Name</Label>
              {profile.name}
            </ProfileItem>

            {profile.profileConfig.sections.map((section) => {
              const sectionWidgetData = profile.widgetData?.[section.key];
              if (!sectionWidgetData?.widgets.length) {
                return null;
              }

              const isHovering = hoveringSection === section.key;

              return (
                <ProfileItem
                  key={section.key}
                  onMouseEnter={() => setHoveringSection(section.key)}
                  onMouseLeave={() => setHoveringSection(null)}
                >
                  <ProfileAccordionContainer
                    profileKey={section.key}
                    renderChildren={() => (
                      <SectionContent>
                        {sectionWidgetData.widgets.map((widget) => (
                          <div key={widget.id}>{renderProfileWidget(widget)}</div>
                        ))}
                      </SectionContent>
                    )}
                  >
                    <Label>
                      {section.name}
                      {section.infoTooltip && <AITooltip helpText={section.infoTooltip} />}
                      {section.aiTooltip && <AITooltip helpText={section.aiTooltip} />}
                      <LearnMore
                        open={isHovering}
                        question={buildSectionLearnMoreQuestion(profile.name, section.name)}
                        showDirectSubmit={showDirectSubmitLearnMore}
                        onDirectSubmit={onDirectSubmitLearnMore}
                      />
                    </Label>
                  </ProfileAccordionContainer>
                  {isHovering && section.isAI && <SmallHint>AI-configured section</SmallHint>}
                </ProfileItem>
              );
            })}

            {quickActions.length > 0 && (
              <ProfileItem>
                <ProfileAccordionContainer
                  profileKey="account-quick-actions"
                  renderChildren={() => (
                    <QuickActionsList>
                      {quickActions.map((action) => (
                        <MessageQueryHint
                          key={action.displayValue}
                          hint={{ type: ChatHintType.Text, displayValue: action.displayValue }}
                          onClick={() => setChatInputText(action.value, { focus: true })}
                        />
                      ))}
                    </QuickActionsList>
                  )}
                >
                  <Label>Quick Actions</Label>
                </ProfileAccordionContainer>
              </ProfileItem>
            )}

            {profile.disclaimer && <Guidelines>{profile.disclaimer}</Guidelines>}

            <Footer>
              {profile.messageActions.copy && copyChatMessages && <CopyButton onClick={handleCopy} />}
              {profile.messageActions.readAloud && <ReadAloudButton queryId={queryId} />}
            </Footer>

            <StyledCitationsPane
              title={`Sources (${getCitationsDisplayCount(citations)})`}
              open={citationsOpen}
              toggleOpen={toggleCitationsOpen}
            >
              <StyledMessageCitations items={citations} />
            </StyledCitationsPane>

            {followupQuestions.length > 0 && (
              <StyledRelatedQuestions
                profileKey="account"
                queryIdentifier={queryId.toString()}
                hints={followupQuestions.map((question) => ({
                  displayValue: question,
                  type: ChatHintType.Text,
                }))}
              />
            )}
          </MessageBody>
        </BasicSystemUserMessage>
      </MessageContainer>
    );
  }
);
